import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { FaPencilAlt, FaEye } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router';
import { useIncidentActions } from './hooks/incidentActionHooks';
import { useOrgActions } from './hooks/orgActionHooks';
import LegacyPratusTable from '../DesignSystems/PratusTable/LegacyPratusTable';
import Loading from '../Loading/Loading';
import type { ColumnDescription } from 'react-bootstrap-table-next';
import { IapScope, INCIDENT } from './ICSConstants';
import ActionExpandedRow from './ActionExpandedRow';
import { ActionEntity } from './hooks/useScopedActionForm';

interface ActionsTableProps {
  scope?: IapScope;
  onEditAction: (action: any) => void;
}

const ActionsTable: React.FC<ActionsTableProps> = ({
  scope = INCIDENT,
  onEditAction,
}) => {
  const isIncident = scope === INCIDENT;
  const incidentQuery = useIncidentActions(scope);
  const orgQuery = useOrgActions(scope);
  const { data: actions, isLoading } = isIncident ? incidentQuery : orgQuery;

  const navigate = useNavigate();
  const { incidentId } = useParams<{ incidentId: string }>();

  const isOverdue = (status: string, targetDate: Date | null) => {
    if (!targetDate) return false;
    const now = new Date();
    const target = new Date(targetDate);
    return (
      (status === 'Current/Ongoing' || status === 'Closed') && target < now
    );
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return dayjs(date).format('MM/DD/YYYY HH:mm');
  };

  const columns: ColumnDescription[] = useMemo(
    () => [
      {
        dataField: 'index',
        text: 'No.',
        sort: true,
        style: { width: '60px' },
        headerStyle: { width: '60px' },
        attrs: { title: 'Number' },
      },
      {
        dataField: 'name',
        text: 'Item (Task/Action)',
        sort: true,
        formatter: (cell: string) => (
          <div>
            <strong>{cell}</strong>
          </div>
        ),
        attrs: { title: 'Item (Task/Action)' },
      },
      {
        dataField: 'poc_user_name',
        text: 'For/POC',
        sort: true,
        formatter: (cell: string) => cell || 'Unassigned',
        style: { width: '150px' },
        headerStyle: { width: '150px' },
        attrs: { title: 'For/POC' },
      },
      {
        dataField: 'briefed_poc',
        text: 'Briefed POC',
        formatter: (cell: boolean) =>
          cell ? <span className="text-success fw-bold">✓</span> : '',
        style: { width: '100px', textAlign: 'center' as const },
        headerStyle: { width: '100px', textAlign: 'center' as const },
        attrs: { title: 'Briefed POC' },
      },
      {
        dataField: 'start_date',
        text: 'Start Date',
        sort: true,
        formatter: (cell: Date) => formatDate(cell),
        style: { width: '140px' },
        headerStyle: { width: '140px' },
        attrs: { title: 'Start Date' },
      },
      {
        dataField: 'status',
        text: 'Status',
        sort: true,
        formatter: (cell: string, row: any) => {
          const overdue = isOverdue(cell, row.target_date);
          return (
            <div>
              <span
                className={`badge ${
                  cell === 'Planned'
                    ? 'bg-info'
                    : cell === 'Current/Ongoing'
                      ? 'bg-warning'
                      : cell === 'Closed'
                        ? 'bg-success'
                        : 'bg-secondary'
                } ${overdue ? 'bg-danger' : ''}`}
              >
                {cell}
              </span>
              {overdue && (
                <div className="text-danger small fw-bold mt-1">Overdue</div>
              )}
            </div>
          );
        },
        style: { width: '120px' },
        headerStyle: { width: '120px' },
        attrs: { title: 'Status' },
      },
      {
        dataField: 'target_date',
        text: 'Target Date',
        sort: true,
        formatter: (cell: Date) => formatDate(cell),
        style: { width: '140px' },
        headerStyle: { width: '140px' },
        attrs: { title: 'Target Date' },
      },
      {
        dataField: 'actual_date',
        text: 'Actual Date',
        sort: true,
        formatter: (cell: Date) => formatDate(cell),
        style: { width: '140px' },
        headerStyle: { width: '140px' },
        attrs: { title: 'Actual Date' },
      },
      ...(isIncident
        ? [
            {
              dataField: 'view',
              text: 'View',
              isDummyField: true,
              formatter: (_: any, row: any) => (
                <button
                  className="btn btn-sm btn-link p-0"
                  onClick={() =>
                    navigate(
                      `/iap/${incidentId}/actions/${row.action_id || row.id}`
                    )
                  }
                  title="View action details"
                >
                  <FaEye />
                </button>
              ),
              style: { width: '60px', textAlign: 'center' as const },
              headerStyle: { width: '60px', textAlign: 'center' as const },
              attrs: { title: 'View' },
            },
          ]
        : []),
      {
        dataField: 'edit',
        text: 'Edit',
        isDummyField: true,
        formatter: (_: any, row: any) => (
          <button
            className="btn btn-sm btn-link p-0"
            onClick={() => onEditAction(row)}
            title="Edit action"
          >
            <FaPencilAlt />
          </button>
        ),
        style: { width: '60px', textAlign: 'center' as const },
        headerStyle: { width: '60px', textAlign: 'center' as const },
        attrs: { title: 'Edit' },
      },
    ],
    [onEditAction, navigate, incidentId, isIncident]
  );

  if (isLoading) {
    return <Loading />;
  }

  const tableData =
    actions?.map((action, index) => ({
      ...action,
      index: index + 1,
      poc_user_name:
        (action as any).poc_user_name || (action as any).user_name || '',
      resources: (action as any).resources || [],
    })) || [];

  const expandRow = {
    parentClassName: 'parent-expand-tr',
    className: 'child-expand-tr',
    renderer: (row: ActionEntity) => <ActionExpandedRow row={row} />,
    showExpandColumn: true,
  };

  return (
    <LegacyPratusTable
      keyField="id"
      columns={columns}
      rows={tableData}
      expandRow={expandRow}
      noDataIndication="No actions found"
    />
  );
};

export default ActionsTable;
